import { useCallback, useEffect, useState } from "react";
import { classNames, formatCurrency } from "helpers";
import { downloadCsv, toCsv } from "helpers/csv";
import Button from "ui/components/Button/Button/Button";
import { getFinanceReceivablesAPI } from "services/api/reports/finance/api";
import { ReceivablesReport } from "services/api/reports/finance/types";
import { useDashboardDateRange } from "ui/modules/dashboard/pages/DashboardPage/context/DashboardDateRangeContext";
import { TableSearch } from "ui/components/Table/TableSearch";
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";
import { initialPagination } from "ui/components/Table/consts";
import { AGING_COLORS, OPEN_INVOICE_COLUMNS, buildReceivablesKpis } from "./consts";

const ReceivablesTab: React.FC = () => {
  const { range } = useDashboardDateRange();
  const [data, setData] = useState<ReceivablesReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getFinanceReceivablesAPI(range.from, range.to)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [range.from, range.to]);

  // Izvoze se samo otvoreni računi — plaćeni nisu ono što računovodstvo goni.
  const handleExport = useCallback(() => {
    if (!data) return;

    const rows = data.invoices.map((row) => ({
      Broj: row.invoiceNumber,
      Kupac: row.clientName,
      Vozilo: row.vehicleName,
      Datum: row.invoiceDate,
      Dospijeće: row.dueDate,
      Iznos: row.amount,
      "Dana kašnjenja": row.daysOverdue,
    }));

    downloadCsv(
      toCsv(rows, [
        "Broj",
        "Kupac",
        "Vozilo",
        "Datum",
        "Dospijeće",
        "Iznos",
        "Dana kašnjenja",
      ]),
      "otvoreni-racuni.csv"
    );
  }, [data]);

  if (isLoading) {
    return (
      <div className="relative min-h-[200px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (!data) return <></>;

  return (
    <div className="px-0 py-6 xl:px-6 space-y-4">
      <div className="flex justify-end">
        <Button variant="outlined" size="small" onClick={handleExport}>
          Izvezi
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {buildReceivablesKpis(data).map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-sm ring-1 ring-slate-200 bg-white px-4 py-3"
          >
            <div className="text-xs uppercase text-slate-400 font-semibold">
              {kpi.label}
            </div>
            <div className={classNames("text-xl font-bold", kpi.valueClass)}>
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-sm border">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold text-slate-800">
          Starosna struktura potraživanja
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100">
          {data.aging.map((bucket) => (
            <div key={bucket.label} className="px-4 py-3">
              <div className="text-xs text-slate-400">{bucket.label}</div>
              <div
                className={classNames(
                  "text-lg font-semibold",
                  AGING_COLORS[bucket.label] ?? "text-slate-700"
                )}
              >
                {formatCurrency.format(bucket.amount)}
              </div>
              <div className="text-xs text-slate-400">{bucket.count} računa</div>
            </div>
          ))}
        </div>
      </div>

      {data.invoices.length === 0 ? (
        <NoResults message="Nema otvorenih računa" />
      ) : (
        <TableSearch
          columns={OPEN_INVOICE_COLUMNS}
          data={data.invoices}
          selectableRows={false}
          disablePagination={true}
          pagination={{
            ...initialPagination,
            totalCount: data.invoices.length,
            totalPages: 1,
          }}
        />
      )}
    </div>
  );
};

export default ReceivablesTab;
